/**
 * Filter chips of the Lines panel (W2-07; DESIGN.md §4.10 "Filter chips (P0)": Visible now, Animating now, Has
 * notes, by group, by type; E§4.15 RPE VisL / OnEve and the group filter). Chips combine (AND); several groups or
 * types combine with OR. The text search lives in the panel header and is part of the same filter state.
 * In a narrow dock the three toggle chips show their icons only (name kept for screen readers, help in the tooltip),
 * so the chips stay on one row (UX-051).
 */

import { memo } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { ChartDoc } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import { Checkbox } from '../../ui/Checkbox';
import { Icon } from '../../ui/Icon';
import { Popover } from '../../ui/Popover';
import { Swatch } from '../../ui/Swatch';
import { m } from './lines.messages';
import {
  filtersActive,
  LINE_TYPE_IDS,
  NO_FILTERS,
  NO_GROUP,
  TYPE_ICONS,
  type GroupFilterValue,
  type LineFilterState,
  type LineTable,
  type LineTypeId,
} from './lineModel';
import { linesUi } from './linesUi';
import s from './Lines.module.css';

export interface LineFiltersProps {
  readonly services: AppServices;
  readonly doc: ChartDoc;
  readonly table: LineTable;
  readonly filters: LineFilterState;
}

function toggled<T>(set: ReadonlySet<T>, v: T, on: boolean): ReadonlySet<T> {
  const next = new Set(set);
  if (on) next.add(v);
  else next.delete(v);
  return next;
}

function LineFiltersImpl(props: LineFiltersProps) {
  const t = useT();
  const { services, doc, table, filters } = props;
  const set = (patch: Partial<LineFilterState>): void => {
    linesUi(services).set({ filters: { ...linesUi(services).get().filters, ...patch } });
  };
  const groups = [...doc.groups.values()].sort((a, b) => a.order - b.order);
  const groupCounts = new Map<GroupFilterValue, number>();
  const typeCounts = new Map<LineTypeId, number>();
  for (const info of table.byId.values()) {
    const g: GroupFilterValue = info.group ?? NO_GROUP;
    groupCounts.set(g, (groupCounts.get(g) ?? 0) + 1);
    typeCounts.set(info.type, (typeCounts.get(info.type) ?? 0) + 1);
  }
  const groupOptions: readonly {
    readonly id: GroupFilterValue;
    readonly name: string;
    readonly color: string | null;
  }[] = [
    { id: NO_GROUP, name: t(m.groupDefault), color: null },
    ...groups.map((g) => ({ id: g.id, name: g.name, color: g.color })),
  ];
  const groupLabel =
    filters.groups.size === 0
      ? t(m.chipGroup)
      : filters.groups.size === 1
        ? t(m.chipGroupOne, {
            name:
              [...filters.groups].map((g) =>
                g === NO_GROUP ? t(m.groupDefault) : (doc.groups.get(g)?.name ?? '?'),
              )[0] ?? '',
          })
        : t(m.chipGroupMany, { count: filters.groups.size });
  const typeLabel =
    filters.types.size === 0
      ? t(m.chipType)
      : filters.types.size === 1
        ? t(m.typeName, { type: [...filters.types][0] ?? 'line' })
        : t(m.chipTypeMany, { count: filters.types.size });

  const chip = (key: 'visibleNow' | 'animatingNow' | 'hasNotes', icon: string, label: string, help: string) => (
    <button
      type="button"
      className={s.chip}
      aria-pressed={filters[key]}
      title={help}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={() => {
        set({ [key]: !filters[key] });
      }}
    >
      <Icon name={icon} size={12} />
      <span className={s.chipText}>{label}</span>
    </button>
  );

  return (
    <div className={s.chips} role="group" aria-label={t(m.filtersLabel)}>
      {chip('visibleNow', 'Eye', t(m.chipVisibleNow), t(m.chipVisibleNowHelp))}
      {chip('animatingNow', 'Activity', t(m.chipAnimatingNow), t(m.chipAnimatingNowHelp))}
      {chip('hasNotes', 'Music2', t(m.chipHasNotes), t(m.chipHasNotesHelp))}
      <Popover
        label={t(m.chipGroup)}
        trigger={
          <button type="button" className={s.chip} data-on={filters.groups.size > 0 ? 'true' : undefined}>
            <Icon name="Tag" size={12} />
            {groupLabel}
            <Icon name="ChevronDown" size={12} />
          </button>
        }
      >
        <div className={s.checkList}>
          {groupOptions.map((g) => (
            <div key={String(g.id)} className={s.checkRow}>
              <Checkbox
                label={t(m.filterOption, { name: g.name, count: groupCounts.get(g.id) ?? 0 })}
                checked={filters.groups.has(g.id)}
                onChange={(on) => {
                  set({ groups: toggled(filters.groups, g.id, on) });
                }}
              />
              {g.color !== null && <Swatch color={g.color} size={10} />}
            </div>
          ))}
          <button
            type="button"
            className={s.linkButton}
            onClick={() => {
              linesUi(services).set({ modal: { kind: 'groups' } });
            }}
          >
            {t(m.menuEditGroups)}
          </button>
        </div>
      </Popover>
      <Popover
        label={t(m.chipType)}
        trigger={
          <button type="button" className={s.chip} data-on={filters.types.size > 0 ? 'true' : undefined}>
            <Icon name="Shapes" size={12} />
            {typeLabel}
            <Icon name="ChevronDown" size={12} />
          </button>
        }
      >
        <div className={s.checkList}>
          {LINE_TYPE_IDS.map((type) => (
            <div key={type} className={s.checkRow}>
              <Icon name={TYPE_ICONS[type]} size={14} />
              <Checkbox
                label={t(m.filterOption, { name: t(m.typeName, { type }), count: typeCounts.get(type) ?? 0 })}
                checked={filters.types.has(type)}
                onChange={(on) => {
                  set({ types: toggled(filters.types, type, on) });
                }}
              />
            </div>
          ))}
        </div>
      </Popover>
      {filtersActive(filters) && (
        <button
          type="button"
          className={s.linkButton}
          onClick={() => {
            linesUi(services).set({ filters: NO_FILTERS });
          }}
        >
          {t(m.clearFilters)}
        </button>
      )}
    </div>
  );
}

/** Re-renders only when the table or the filters change (not on every Lines panel render, UX-053). */
export const LineFilters = memo(LineFiltersImpl);
